import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiAlertCircle, FiSave } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Container = styled.div`
  background: var(--background-primary);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);

  h2 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--text-primary);
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--text-primary);
    background: var(--background-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  padding: 2rem;
  min-height: 300px;
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;

  .required {
    color: #f44336;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-secondary);
  color: var(--text-primary);
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-secondary);
  color: var(--text-primary);
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid #f44336;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: #f44336;
  display: flex;
  gap: 0.75rem;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.primary {
    background: var(--primary-color);
    color: white;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  }

  &.secondary {
    background: transparent;
    border: 1px solid var(--border-color);
    color: var(--text-primary);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EditExternalItemModal = ({ item, itemType, courseId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    points: '',
    dueDate: '',
    dueAt: '',
    externalSourceName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        description: item.description || '',
        points: item.points || '',
        dueDate: item.dueDate || '',
        dueAt: item.dueAt || '',
        externalSourceName: item.externalSourceName || ''
      });
    }
  }, [item]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData.points || isNaN(formData.points) || formData.points < 1 || formData.points > 1000) {
      setError('Points must be a number between 1 and 1000');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const updateMethod = itemType === 'assignment'
        ? apiService.updateExternalAssignment
        : apiService.updateExternalQuiz;

      const payload = {
        title: formData.title,
        points: parseInt(formData.points),
        ...(formData.description && { description: formData.description }),
        ...(formData.externalSourceName && { externalSourceName: formData.externalSourceName }),
        ...(itemType === 'assignment' && formData.dueDate && { dueDate: formData.dueDate }),
        ...(itemType === 'quiz' && formData.dueAt && { dueAt: formData.dueAt })
      };

      const result = await updateMethod.call(apiService, item.id, parseInt(courseId), payload);

      if (result.success) {
        onSuccess?.();
        onClose?.();
      } else {
        // Handle validation errors from express-validator
        if (result.errors && Array.isArray(result.errors)) {
          const errorMessages = result.errors.map(e => `${e.param}: ${e.msg}`).join(', ');
          setError(errorMessages);
        } else {
          setError(result.message || 'Failed to update external item');
        }
      }
    } catch (err) {
      logger.error('Error updating external item', err);
      setError(err.message || 'Failed to update external item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <h2>Edit {itemType === 'assignment' ? 'Assignment' : 'Quiz'}</h2>
          <CloseButton onClick={onClose} disabled={loading}>
            <FiX size={24} />
          </CloseButton>
        </Header>

        <Content>
          {error && (
            <ErrorBox>
              <FiAlertCircle size={20} />
              <span>{error}</span>
            </ErrorBox>
          )}

          <FormGroup>
            <Label>
              Title <span className="required">*</span>
            </Label>
            <Input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={loading}
              maxLength="255"
            />
          </FormGroup>

          <FormGroup>
            <Label>Max Points <span className="required">*</span></Label>
            <Input
              type="number"
              name="points"
              value={formData.points}
              onChange={handleInputChange}
              min="1"
              max="1000"
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              Due Date
              {itemType === 'assignment' && ' (Assignment)'}
              {itemType === 'quiz' && ' (Quiz)'}
            </Label>
            <Input
              type="date"
              name={itemType === 'assignment' ? 'dueDate' : 'dueAt'}
              value={itemType === 'assignment' ? formData.dueDate : formData.dueAt}
              onChange={handleInputChange}
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label>External Source Name</Label>
            <Input
              type="text"
              name="externalSourceName"
              value={formData.externalSourceName}
              onChange={handleInputChange}
              disabled={loading}
              maxLength="255"
            />
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <TextArea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              disabled={loading}
            />
          </FormGroup>

          <ButtonGroup>
            <Button
              className="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              className="primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                <>
                  <LoadingSpinner /> Saving...
                </>
              ) : (
                <>
                  <FiSave size={18} />
                  Save Changes
                </>
              )}
            </Button>
          </ButtonGroup>
        </Content>
      </Container>
    </Overlay>
  );
};

export default EditExternalItemModal;
